import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Select } from "../ui";
import { useToasts } from "../ui/toasts";
import { listConnections, openAsIssue, type RemoteConnection } from "../core/remote";
import type { Task } from "../core/schema";

const cache = new Map<string, Promise<RemoteConnection[]>>();

/**
 * "Open as issue" (spec §9.22): offered on plain tasks when the workspace has a connection to an issue
 * tracker. Creates the issue from title and notes; the task then mirrors it.
 */
export function RemoteActions({ task, workspaceId }: { task: Task; workspaceId: string }) {
  const { t } = useTranslation();
  const [conns, setConns] = useState<RemoteConnection[]>([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToasts((s) => s.push);

  useEffect(() => {
    let live = true;
    if (!cache.has(workspaceId))
      cache.set(
        workspaceId,
        listConnections(workspaceId).catch(() => []),
      );
    void cache.get(workspaceId)!.then((c) => live && setConns(c));
    return () => {
      live = false;
    };
  }, [workspaceId]);

  const options = conns.flatMap((c) =>
    c.repos.map((repo) => ({ key: `${c.type}|${c.id}|${repo}`, c, repo })),
  );
  if (task.typeId !== "task" || options.length === 0) return null;
  const chosen = options.find((o) => o.key === target) ?? options[0];

  const open = async () => {
    setBusy(true);
    try {
      await openAsIssue(workspaceId, task.id, chosen.c, chosen.repo);
      toast(t("remote.opened", { repo: chosen.repo }));
    } catch (e) {
      toast(t(`errors.${(e as Error).message}`, { defaultValue: t("remote.failed") }));
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="remote-actions">
      {options.length > 1 && (
        <Select
          aria-label={t("remote.repository")}
          value={chosen.key}
          onChange={(e) => setTarget(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.key} value={o.key}>
              {o.repo}
            </option>
          ))}
        </Select>
      )}
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={() => void open()}
        data-testid="open-as-issue"
      >
        {t("remote.openAs", { repo: chosen.repo })}
      </Button>
    </div>
  );
}
